import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ModalsWrapperContext } from '@kanda-libs/ks-component-ts';
import { type FinanceRate } from '@kanda-libs/ks-frontend-services';

import rateSelectors from 'store/selectors/rate';

import { MODAL_ID } from './constants';
import { hasIntersection } from './helpers';

import useCompany from '../useCompany';

export interface CompanyCustomRatesHook {
  onShowModal: () => void;
  allRates: FinanceRate[];
  existing: FinanceRate[] | null;
  hasExisting: boolean;
  isLoading: boolean;
}

export default function useCompanyCustomRates(): CompanyCustomRatesHook {
  const { showModal } = useContext(ModalsWrapperContext);
  const { company } = useCompany();
  const rates = useSelector(rateSelectors.getEntities);
  const hasFetched = useSelector(rateSelectors.getHasFetched);

  const isLoading = useMemo(() => !hasFetched, [hasFetched]);

  const id = useMemo(() => company?.cid, [company]);

  const allRates = useMemo(() => {
    if (!rates || !company) return [];
    const companyWorkTypes = company.company_info?.work_types;
    if (!companyWorkTypes || companyWorkTypes.length === 0) return [];
    const ratesForAll = rates?.['undefined'];
    if (!ratesForAll) return [];
    return ratesForAll.finance_rates.filter((r) =>
      r.work_types ? hasIntersection(r.work_types, companyWorkTypes) : r,
    );
  }, [rates, company]);

  const existing = useMemo(() => {
    if (!id) return [];
    const companyRates = rates?.[id];
    if (!companyRates) return [];
    return companyRates.finance_rates;
  }, [id, rates]);

  const hasExisting = useMemo(() => {
    if (!existing || existing.length === 0) return false;
    const lenders = existing.filter((v, i, a) => a.indexOf(v) === i);
    return lenders.length === 1;
  }, [existing]);

  const onShowModal = useCallback(() => {
    showModal(MODAL_ID);
  }, [showModal]);

  return { onShowModal, allRates, existing, hasExisting, isLoading };
}
